import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import FilterSelect from '../components/FilterSelect';
import { toast } from 'react-toastify';

const GENDER_OPTIONS = [
  { value: '', label: 'Select' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' },
];

function UserRegister() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  /* ---------------- CSRF INITIALIZATION ---------------- */
  useEffect(() => {
    const initCsrf = async () => {
      try {
        await api.get('user/login/');
      } catch (err) {
        console.error("CSRF Init failed", err);
      }
    };
    initCsrf();
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    gender: '',
    phone_number: '',
    address: '',
    age: '',
    height: '',
    weight: '',
    dateofbirth: '',
    disease_description: '',
    medical_record: null,
    image: null,
  });

  const [imagePreview, setImagePreview] = useState(null);

  const [passwordValid, setPasswordValid] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const [showPasswordHint, setShowPasswordHint] = useState(false);

  const validatePassword = (pass) => {
    setPasswordValid({
      length: pass.length >= 8,
      uppercase: /[A-Z]/.test(pass),
      lowercase: /[a-z]/.test(pass),
      number: /\d/.test(pass),
      special: /[@$!%*#?&]/.test(pass)
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === 'password') {
      validatePassword(value);
    }
  };

  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.files[0],
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        image: file,
      });
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData({
        ...formData,
        image: null,
      });
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.gender) {
      toast.warning("Please select your gender.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    // 🔐 Password Strength Check
    const password = formData.password;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;
    
    if (!passwordRegex.test(password)) {
      toast.warning("Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, 1 special character and minimum 8 characters.", {
        position: "bottom-right",
        autoClose: 4000,
      });
      return;
    }

    // 🖼️ Image Required Check
    if (!formData.image || !(formData.image instanceof File)) {
      toast.warning("Please upload a profile picture. This field is required.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      // Only append image if it's a File object
      if (key === 'image') {
        if (formData[key] instanceof File) {
          data.append(key, formData[key]);
        }
      } else if (formData[key] !== null && formData[key] !== '') {
        data.append(key, formData[key]);
      }
    });

    try {
      console.log('📤 Sending registration request...');
      console.log('API Base URL:', process.env.REACT_APP_API_URL);
      
      const response = await api.post('user/register/', data, {
        timeout: 30000, // 30 seconds timeout
      });

      console.log('✅ Registration response received:', response);
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);

      // ✅ Check if response is successful (status 200-299)
      if (response.status === 201 || response.status === 200) {
        const successMessage = response.data?.msg || response.data?.message || 'Registration successful! Please login.';
        toast.success(successMessage, {
          position: "bottom-right",
          autoClose: 3000,
        });
        navigate('/user/login');
      } else {
        toast.error('Registration failed. Please try again.', {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error('❌ Registration error:', err);
      console.error('Error type:', err.code);
      console.error('Error message:', err.message);
      console.error('Has response:', !!err.response);
      console.error('Has request:', !!err.request);
      
      // ✅ Better error handling
      if (err.response) {
        // Server responded with error status
        const errorMsg = err.response.data?.msg || 
                        err.response.data?.message || 
                        err.response.data?.error ||
                        (typeof err.response.data === 'object' ? JSON.stringify(err.response.data) : err.response.data) ||
                        'Registration failed. Please check your details.';
        toast.error('Registration failed: ' + errorMsg, {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else if (err.request) {
        // Request made but no response received
        console.error('Request was made but no response received');
        console.error('Check if backend is running at:', process.env.REACT_APP_API_URL);
        toast.error(`Registration failed: No response from server.\n\nPlease check:\n1. Backend server is running\n2. API URL: ${process.env.REACT_APP_API_URL || 'Not set'}\n3. Check browser console for details`, {
          position: "bottom-right",
          autoClose: 5000,
        });
      } else {
        // Something else happened
        console.error('Error setting up request:', err.message);
        toast.error('Registration failed: ' + err.message, {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-light min-vh-100 py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-7">
            
            <div className="text-center mb-4">
              <div className="bg-primary d-inline-flex p-3 rounded-circle shadow-sm mb-3">
                <i className="bi bi-person-plus-fill fs-2 text-white"></i>
              </div>
              <h2 className="fw-bold text-primary">Join FITNOVA</h2>
              <p className="text-muted">Create your health profile and start your wellness journey.</p>
            </div>

            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-4 p-md-5">
                <form onSubmit={handleSubmit}>
                  
                  <h5 className="fw-bold mb-4 pb-2 border-bottom">
                    <i className="bi bi-info-circle me-2 text-primary"></i>Account Information
                  </h5>

                  

                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Full Name</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control bg-light"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        className="form-control bg-light"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Password</label>
                      <input
                        type="password"
                        name="password"
                        className="form-control bg-light"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        onFocus={() => setShowPasswordHint(true)}
                        onBlur={() => setShowPasswordHint(false)}
                        required
                      />
                      {showPasswordHint && (
                        <div className="card shadow-sm mt-2 p-3 position-absolute z-3 w-100" style={{ maxWidth: '300px' }}>
                          <h6 className="small fw-bold border-bottom pb-2 mb-2">Password Requirements:</h6>
                          <div className={`small mb-1 ${passwordValid.length ? 'text-success' : 'text-muted'}`}>
                            <i className={`bi bi-${passwordValid.length ? 'check-circle-fill' : 'circle'} me-2`}></i>
                            At least 8 characters
                          </div>
                          <div className={`small mb-1 ${passwordValid.uppercase ? 'text-success' : 'text-muted'}`}>
                            <i className={`bi bi-${passwordValid.uppercase ? 'check-circle-fill' : 'circle'} me-2`}></i>
                            One uppercase letter
                          </div>
                          <div className={`small mb-1 ${passwordValid.lowercase ? 'text-success' : 'text-muted'}`}>
                            <i className={`bi bi-${passwordValid.lowercase ? 'check-circle-fill' : 'circle'} me-2`}></i>
                            One lowercase letter
                          </div>
                          <div className={`small mb-1 ${passwordValid.number ? 'text-success' : 'text-muted'}`}>
                            <i className={`bi bi-${passwordValid.number ? 'check-circle-fill' : 'circle'} me-2`}></i>
                            One number
                          </div>
                          <div className={`small ${passwordValid.special ? 'text-success' : 'text-muted'}`}>
                            <i className={`bi bi-${passwordValid.special ? 'check-circle-fill' : 'circle'} me-2`}></i>
                            One special character (@$!%*#?&)
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Phone Number</label>
                      <input
                        type="tel"
                        name="phone_number"
                        maxLength={10}
                        className="form-control bg-light"
                        placeholder="+1 234 567 890"
                        value={formData.phone_number}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                   
                   {/* Profile Image Upload */}
                   <div className="row g-3 mb-4">
                     <div className="col-12">
                       <label className="form-label small fw-bold">
                         Profile Picture <span className="text-danger">*</span>
                       </label>
                       <div className="d-flex align-items-center gap-4">
                         {imagePreview && (
                           <div className="position-relative">
                             <img
                               src={imagePreview}
                               alt="Preview"
                               className="rounded-circle border border-2 border-primary"
                               style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                             />
                           </div>
                         )}
                         <div className="flex-grow-1">
                           <input
                             type="file"
                             name="image"
                             className="form-control bg-light"
                             accept="image/*"
                             onChange={handleImageChange}
                             required
                           />
                           <small className="text-muted">Upload a profile picture (JPG, PNG, etc.) - Required</small>
                         </div>
                       </div>
                     </div>
                   </div>







                  <h5 className="fw-bold mb-4 pb-2 border-bottom">
                    <i className="bi bi-heart-pulse me-2 text-primary"></i>Health Profile
                  </h5>

                  <div className="row g-3 mb-4">
                    <div className="col-md-4">
                      <label className="form-label small fw-bold">Gender</label>
                      <FilterSelect
                        value={formData.gender}
                        onChange={(gender) => setFormData({ ...formData, gender })}
                        options={GENDER_OPTIONS}
                        placeholder="Select"
                        aria-label="Gender"
                        variant="light"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-bold">Age</label>
                      <input
                        type="number"
                        name="age"
                        className="form-control bg-light"
                        placeholder="25"
                        value={formData.age}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-bold">Date of Birth</label>
                      <input
                        type="date"
                        name="dateofbirth"
                        className="form-control bg-light"
                        value={formData.dateofbirth}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Height (cm)</label>
                      <input
                        type="number"
                        step="0.1"
                        name="height"
                        className="form-control bg-light"
                        placeholder="175.5"
                        value={formData.height}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Weight (kg)</label>
                      <input
                        type="number"
                        step="0.1"
                        name="weight"
                        className="form-control bg-light"
                        placeholder="70.2"
                        value={formData.weight}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold">Address</label>
                      <textarea
                        name="address"
                        className="form-control bg-light"
                        placeholder="Street, City, Country"
                        rows="2"
                        value={formData.address}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold">Disease Description</label>
                      <textarea
                        name="disease_description"
                        className="form-control bg-light"
                        placeholder="Describe any existing conditions"
                        rows="2"
                        value={formData.disease_description}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold">Medical Record (PDF/Image)</label>
                      <input
                        type="file"
                        name="medical_record"
                        className="form-control bg-light"
                        onChange={handleFileChange}
                      />
                    </div>
                  </div>

                  <div className="d-grid gap-3 pt-3">
                    <button 
                      type="submit" 
                      className="btn btn-primary btn-lg fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center"
                      disabled={loading}
                    >
                      {loading && <span className="spinner-border spinner-border-sm me-2"></span>}
                      Register Account
                    </button>
                    
                    <button 
                      type="button" 
                      className="btn btn-link btn-sm text-decoration-none text-muted"
                      onClick={() => navigate('/user/login')}
                    >
                      Already have an account? <span className="text-primary fw-bold">Login here</span>
                    </button>
                  </div>

                </form>
              </div>
            </div>

            <div className="text-center mt-4">
              <button 
                className="btn btn-link btn-sm text-muted text-decoration-none"
                onClick={() => navigate(-1)}
              >
                <i className="bi bi-arrow-left me-1"></i> Back
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default UserRegister;
